import { GetServerSidePropsContext, InferGetServerSidePropsType } from 'next'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useState } from 'react'
import { ArticleList } from '../components/ArticleList'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { Contained } from '../components/Contained'
import { Content } from '../components/Content'
import { Input } from '../components/Input'
import { Intro } from '../components/Intro'
import { LightTabs } from '../components/LightTabs'
import { MissingContent } from '../components/MissingContent'
import { Pagination } from '../components/Pagination'
import { RadioButtons } from '../components/RadioButtons'
import { SearchResultsTitle } from '../components/SearchResultsTitle'
import { Seo } from '../components/Seo'
import { Spacer } from '../components/Spacer'
import { WidthBound } from '../components/WidthBound'
import { useCurrentLocale } from '../src/contexts/CurrentLanguageContext'
import { useDebounce } from '../src/hooks/useDebounce'
import { usePageLink } from '../src/hooks/usePageLink'
import { useTranslate } from '../src/hooks/useTranslate'
import {
	ArticleCategoryFragment,
	ArticleFragment,
	PageFragment,
	SpecialPageType,
	UUID,
} from '../src/model/fragments'
import {
	DEFAULT_LOCALE,
	getLocale,
	getPossibleLocales,
	Locale,
} from '../src/model/locale'
import { getBlogCategoryLink, getLinkWithQuery } from '../src/model/urls'
import { axiosGet } from '../src/utils/axiosWrapper'
import { createPaginator } from '../src/utils/pagination'
import { parseQueryInt } from '../src/utils/query'

const PER_PAGE = 4

const ArticleSearchBox: React.FC<
	{ currentQuery: string | null, onSearch: (query: string) => void }
> = ({ currentQuery, onSearch }) => {
	const [query, setQuery] = useState(currentQuery || '')
	const debouncedQuery = useDebounce(query, 200)
	const translate = useTranslate();

	useEffect(() => {
		if(debouncedQuery != (currentQuery ?? ''))
		{
			onSearch(debouncedQuery);
		}
	}, [debouncedQuery])

	return (
		<div style={{ maxWidth: '230px' }}>
			<Input
				value={query}
				name="query-small-bland"
				// todo translate
				label={translate('search') + ':'}
				submitStyle="bland"
				onChange={(e) => {
					const q = e.currentTarget.value;
					setQuery(q)
				}}
				micro
				autoFocus={currentQuery ? true : false}
			/>
		</div>
	)
}

export default function Blog({
	page,
	categories,
	articles,
	possibleLocales,
	pageInfo,
	categoryId,
	topicId,
	searchQuery
}: InferGetServerSidePropsType<typeof getServerSideProps>) {
	
	const locale = useCurrentLocale()
	const translate = useTranslate()
	const router = useRouter();

	const category = categories.find((category) => category.id === categoryId) ?? null;
	const topics = category?.topics || [];
	const topic = topics.find((topic) => topic.id === topicId) ?? null;

	const blogLink = usePageLink(SpecialPageType.blog)!
	const makeLink = React.useCallback((linkCategory: ArticleCategoryFragment | null, linkTopic: {id: UUID} | null, q: string | null) => {

		const query = q?.trim() || '';
		const base = (q || !linkCategory) ? blogLink : getBlogCategoryLink(locale, linkCategory.id, linkCategory.link.url);

		const link = {
			...getLinkWithQuery(base, q ? { q: q!} : {
				topic: linkTopic?.id,
			}),
			scroll: false,
		};

		return link;
	}, [blogLink]);	

	const data = page ? getLocale(page.locales, possibleLocales) : null
	const breadcrumbs = React.useMemo(() => [
		{
			label: data?.title ?? '',
			linkProps: blogLink,
		},
		...(category ? [{label: category.name, linkProps: makeLink(category, null, null)}] : []),
		...(category && topic ? [{label: getLocale(topic.locales, possibleLocales)?.title ?? '', linkProps: makeLink(category, topic, null)}] : []),
		...(searchQuery ? [{label: searchQuery!, linkProps: makeLink(null, null, searchQuery)}] : []),
	], [category, topic, blogLink, searchQuery]);

	if (!page || !data) {
		
		return <MissingContent />
	}


	return (
		<>
			<Seo seo={category?.seo || data.seo} />
			<Breadcrumbs
				items={breadcrumbs}
			/>
			<WidthBound>
				<Intro title={data.title}>
					<Content content={data.content} innerSize="narrow" />
				</Intro>
			</WidthBound>
			<WidthBound>
				<LightTabs
					items={
						!searchQuery
							? [
									{
										isActive: categoryId === null,
										label: 'Vše',
										linkProps: makeLink(null, null, null)
									},
									...categories.map((category) => ({
										isActive: categoryId === category.id,
										label: category.name,
										linkProps: makeLink(category, null, null)
									})),
								]
							: []
					}
					aside={
						<div style={{ maxWidth: '230px' }}>
							<ArticleSearchBox currentQuery={searchQuery} onSearch={q => {

								const link = makeLink(null, null, q);
								router.push(link.href, link.as, {shallow: false});
							}} />
						</div>
					}
				/>
			</WidthBound>
			<Contained>
				<Spacer initial={20} />
				<WidthBound>
					{topics.length > 0 && !searchQuery && (
						<RadioButtons
							key={`${categoryId}-${topicId}`}
							items={[
								{
									text: translate('blog-filter-topic-all'),
									linkProps: makeLink(category, null, null),
								},
								...topics.map((topic) => ({
									text: getLocale(topic.locales, possibleLocales)?.title,
									linkProps: makeLink(category, topic, null),
								})),
							]}
							initialIndex={
								topicId === null
									? 0
									: 1 + topics.findIndex((topic) => topic.id === topicId)
							}
						/>
					)}
					{
						searchQuery && <SearchResultsTitle>{searchQuery}</SearchResultsTitle>
					}
					<ArticleList items={articles} />
					<Spacer initial={20} />
					<Pagination
						currentPage={pageInfo.currentPage}
						pagesCount={pageInfo.lastPage}
					/>
				</WidthBound>
			</Contained>
		</>
	)
}

export const getServerSideProps = async (ctx: GetServerSidePropsContext) => {
	const { offset, limit, getPagesInfo } = createPaginator(
		PER_PAGE,
		parseQueryInt(ctx.query.page),
	)

	const queryParams = ctx.query

	const categoryId =
		typeof queryParams.category === 'string' && queryParams.category
			? queryParams.category
			: null
	const topicId =
		typeof queryParams.topic === 'string' && queryParams.topic
			? queryParams.topic
			: null
	const q =
		typeof queryParams.q === 'string' && queryParams.q
			? queryParams.q
			: null

	const locale = (ctx.query.locale || DEFAULT_LOCALE) as Locale
	const possibleLocales = getPossibleLocales(locale)

	const {
		getPage: page,
		listBlogCategory: categories,
		paginateBlogArticle: {
			pageInfo: { totalCount },
			edges,
		},
	} = await axiosGet('/blog/get', {
		locale: locale,
		q: q,
		topicId: topicId,
		categoryId: categoryId,
		navigationId: ctx.query.id,
		page: ctx.query.page,
		limit: limit
	}) as 
	{
		getPage: PageFragment,
		listBlogCategory: ArticleCategoryFragment[],
		paginateBlogArticle: {
			pageInfo: 
			{
				totalCount: number
			},
			edges: {
				node: ArticleFragment
			}[]
		}
	};

	const articles = edges.map(({ node }) => node);
	return {
		props: {
			page,
			categories,
			articles,
			pageInfo: getPagesInfo(totalCount),
			possibleLocales,
			categoryId,
			topicId,
			searchQuery: q
		},
	}
}
